import { persistentReducedStream, reducer } from '@ardoq/rxbeach';
import {
  persistLoadedDashboardData,
  setDashboardApiError,
  SetDashboardApiErrorPayload,
  setDashboardLoadingState,
} from './streams/actions';
import type { ArdoqId, DashboardFilterValues } from '@ardoq/api-types';
import { LoadingState } from './types';
import { currentDate } from '@ardoq/date-time';
import {
  MAINLINE,
  rememberDashboardColors,
  SelectedBranch,
  type DashboardApiError,
  type LoadedDashboard,
  type RememberedColorsByDashboardId,
} from '@ardoq/dashboard';

const defaultState: LoadedDashboardsState = {
  loadedDashboards: {},
  rememberedColorsByDashboardId: {},
};

export type DashboardAPIRequestState = {
  apiError?: DashboardApiError;
  apiLoadingState?: LoadingState | null;
  apiLoadingDashboardId?: ArdoqId | undefined;
  apiLoadingGlobalFilterValues?: DashboardFilterValues | undefined;
};

/*
{
  loadedDashboards: {
    [dashboardId]: {
      mainline: {...},
      [branchId]: {...}
    }
  }
}
*/
export type LoadedDashboardsState = {
  loadedDashboards: Record<ArdoqId, Record<SelectedBranch, LoadedDashboard>>;
  rememberedColorsByDashboardId: RememberedColorsByDashboardId;
} & DashboardAPIRequestState;

export const setLoadedDashboard = (
  state: LoadedDashboardsState,
  loadedDashboard: LoadedDashboard
): LoadedDashboardsState => {
  const { dashboard: dashboardData, rememberedColorsByDashboardId } =
    rememberDashboardColors(
      loadedDashboard,
      state.rememberedColorsByDashboardId
    );
  return {
    ...state,
    apiError: undefined,
    apiLoadingState: null,
    apiLoadingDashboardId: undefined,
    apiLoadingGlobalFilterValues: undefined,
    loadedDashboards: {
      ...state.loadedDashboards,
      [dashboardData._id]: {
        ...(state.loadedDashboards[dashboardData._id] ?? {}),
        [dashboardData.scenarioBranchId ?? MAINLINE]: {
          ...dashboardData,
          _time: currentDate(),
        },
      },
    },
    rememberedColorsByDashboardId,
  };
};

const setApiErrorAndLoadingState = (
  state: LoadedDashboardsState,
  {
    apiError,
    apiLoadingState = LoadingState.LOADING_ERROR,
  }: SetDashboardApiErrorPayload
): LoadedDashboardsState => ({
  ...state,
  apiError,
  apiLoadingState,
});

const setApiLoadingState = (
  state: LoadedDashboardsState,
  {
    apiLoadingState,
    apiLoadingDashboardId,
    apiLoadingGlobalFilterValues,
  }: Omit<DashboardAPIRequestState, 'apiError'>
): LoadedDashboardsState => ({
  ...state,
  apiLoadingState,
  apiLoadingDashboardId,
  apiLoadingGlobalFilterValues,
  apiError: undefined,
});

export const loadedDashboards$ = persistentReducedStream<LoadedDashboardsState>(
  'loadedDashboards$',
  defaultState,
  [
    reducer(persistLoadedDashboardData, setLoadedDashboard),
    reducer(setDashboardApiError, setApiErrorAndLoadingState),
    reducer(setDashboardLoadingState, setApiLoadingState),
  ]
);
